import { useCallback, useEffect, useMemo, useState } from 'react';
import { Play, Maximize, Droplets, Users, Factory, CheckSquare, Square } from '../lib/icons';
import { toast } from 'sonner';
import {
  fetchFileAttente,
  demarrerTrituration,
  familleLot,
  decompte,
  labelQualite,
  labelMode,
  nomTiers,
  familleDe,
  numeroOf,
  ordreDansOf,
} from '../lib/dataProvider';
import { fmt, StatutBadge } from '../components/shared/badges';
import { SortieHuileDialog } from '../components/trituration/SortieHuileDialog';

const LotCard = ({ lot, selected, multi, onToggle }) => {
  const c = decompte(lot);
  const lamp = familleLot(lot) === 'lampante';
  const of = numeroOf(lot.ofId);
  const ordre = ordreDansOf(lot);
  return (
    <button
      type="button"
      data-testid={`lot-card-${lot.id}`}
      onClick={() => onToggle(lot)}
      className={`w-full border-2 px-4 py-4 text-left transition-colors duration-150 ${
        selected
          ? lamp
            ? 'border-orange-600 bg-orange-50'
            : 'border-slate-900 bg-slate-100'
          : 'border-slate-200 bg-white hover:bg-slate-50'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          {of && (
            <span
              data-testid={`badge-of-${lot.id}`}
              className="mb-1 inline-block border-2 border-slate-900 bg-slate-900 px-2 py-0.5 font-data text-[11px] font-bold uppercase tracking-[0.08em] text-white"
            >
              OF {of} · passage n° {ordre}
            </span>
          )}
          <p className="font-heading text-lg font-black tracking-tight text-slate-900">
            {nomTiers(lot.tiersId)}
          </p>
          <p className="font-data text-xs uppercase tracking-[0.1em] text-slate-500">
            {lot.id} · {lot.date} · {labelMode(lot.mode)}
            {lot.parcours === 'client' && lot.devenir === 'sortie' ? ' · SORTIE IMMÉDIATE' : ''}
          </p>
        </div>
        {multi &&
          (selected ? (
            <CheckSquare size={22} className="flex-shrink-0 text-slate-900" />
          ) : (
            <Square size={22} className="flex-shrink-0 text-slate-300" />
          ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1">
        <span className="tabular font-data text-2xl font-semibold text-slate-900">
          {fmt(c.poidsNet)} <span className="text-sm text-slate-500">Kg</span>
        </span>
        {lot.qualite && (
          <span
            className={`border px-2 py-0.5 font-data text-xs uppercase tracking-[0.06em] ${
              lamp ? 'border-orange-400 bg-orange-100 text-orange-900' : 'border-emerald-300 bg-emerald-50 text-emerald-800'
            }`}
          >
            {labelQualite(lot.qualite)}
          </span>
        )}
        <StatutBadge statut={lot.statut} />
      </div>
    </button>
  );
};

export default function Trituration() {
  const [lots, setLots] = useState([]);
  const [onglet, setOnglet] = useState('client');
  const [selection, setSelection] = useState([]);
  const [sortieOpen, setSortieOpen] = useState(false);

  const load = useCallback(() => {
    fetchFileAttente().then(setLots);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const clients = useMemo(() => lots.filter((l) => l.parcours === 'client'), [lots]);
  const fournisseurs = useMemo(() => lots.filter((l) => l.parcours === 'fournisseur'), [lots]);  const multi = true;
  const visibles = (onglet === 'fournisseur' ? fournisseurs : clients)
    .slice()
    .sort((a, b) => (b.ofId ? 1 : 0) - (a.ofId ? 1 : 0) || (ordreDansOf(a) ?? 99) - (ordreDansOf(b) ?? 99));
  const selLots = lots.filter((l) => selection.includes(l.id));

  const changerOnglet = (o) => {
    setOnglet(o);
    setSelection([]);
  };

  const toggle = (lot) => {
    if (selection.includes(lot.id)) {
      setSelection((s) => s.filter((i) => i !== lot.id));
      return;
    }
    // Plusieurs clients peuvent passer dans le même OF : leurs olives et leur huile
    // restent séparées (la sortie d'huile se déclare client par client).
    const dejaFamille = selLots.length ? familleLot(selLots[0]) : null;
    if (dejaFamille && familleLot(lot) !== dejaFamille) {
      toast.error('Regroupement interdit : lampante et nobles ne se triturent jamais ensemble.');
      return;
    }
    setSelection((s) => [...s, lot.id]);
  };

  const grouperFoss = () => {
    const foss = fournisseurs.filter((l) => l.mode === 'foss' && familleDe(l.qualite) === 'noble');
    if (!foss.length) return toast.error('Aucun achat FOSS nobles en file d’attente.');
    setSelection(foss.map((l) => l.id));
    toast.success(`${foss.length} lot(s) FOSS nobles regroupé(s)`);
  };

  const demarrer = async () => {
    await demarrerTrituration(selection);
    toast.success('Trituration démarrée');
    load();
  };

  const poidsSel = selLots.reduce((s, r) => s + decompte(r).poidsNet, 0);
  const familleSel = selLots.length ? familleLot(selLots[0]) : null;
  const clientsSel = [...new Set(selLots.filter((l) => l.parcours === 'client').map((l) => l.tiersId))];

  return (
    <div data-testid="trituration-page" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">
            🏭 Trituration — Mode Opérateur
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Interface atelier : production en cours uniquement, aucune donnée financière
          </p>
        </div>
        <button
          type="button"
          data-testid="plein-ecran"
          onClick={() => {
            const el = document.documentElement;
            if (document.fullscreenElement) document.exitFullscreen();
            else el.requestFullscreen?.();
          }}
          className="flex h-12 items-center gap-2 border-2 border-slate-300 px-4 font-heading text-sm font-bold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
        >
          <Maximize size={18} /> Plein écran
        </button>
      </div>

      <div data-testid="acces-restreint" className="border-2 border-slate-900 bg-slate-900 px-4 py-3">
        <p className="font-data text-xs uppercase tracking-[0.1em] text-slate-300">
          Accès restreint atelier — file d'attente, pesées et affectation en cuve. Prix, décomptes
          et finances non accessibles depuis ce poste.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2">
        {[
          { id: 'client', label: 'Clients — plusieurs clients possibles', icon: Users, n: clients.length },
          { id: 'fournisseur', label: 'Fournisseurs — groupage possible', icon: Factory, n: fournisseurs.length },
        ].map((o) => {
          const Icon = o.icon;
          const active = onglet === o.id;
          return (
            <button
              key={o.id}
              type="button"
              data-testid={`onglet-${o.id}`}
              onClick={() => changerOnglet(o.id)}
              className={`flex items-center justify-center gap-3 px-4 py-5 font-heading text-base font-bold transition-colors duration-150 ${
                active ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Icon size={20} /> {o.label}
              <span className={`font-data text-sm ${active ? 'text-slate-300' : 'text-slate-500'}`}>
                ({o.n})
              </span>
            </button>
          );
        })}
      </div>

      {multi && (
        <div className="flex flex-wrap gap-3">
          {onglet === 'fournisseur' && (
          <button
            type="button"
            data-testid="grouper-foss"
            onClick={grouperFoss}
            className="h-12 border-2 border-slate-300 px-4 font-heading text-sm font-bold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            Grouper tous les achats FOSS (nobles)
          </button>
          )}
          {onglet === 'client' && (
            <button
              type="button"
              data-testid="grouper-client"
              onClick={() => {
                if (!selLots.length) return toast.error('Sélectionnez d’abord un lot du client.');
                const t = selLots[0].tiersId;
                const memes = clients.filter((l) => l.tiersId === t);
                setSelection(memes.map((l) => l.id));
                toast.success(`${memes.length} lot(s) du même client regroupé(s)`);
              }}
              className="h-12 border-2 border-slate-300 px-4 font-heading text-sm font-bold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              Grouper tous les lots de ce client
            </button>
          )}
          <button
            type="button"
            data-testid="vider-selection"
            onClick={() => setSelection([])}
            className="h-12 border-2 border-slate-300 px-4 font-heading text-sm font-bold text-slate-800 transition-colors duration-150 hover:bg-slate-100"
          >
            Vider la sélection
          </button>
        </div>
      )}

      <div data-testid="file-attente" className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {visibles.length === 0 ? (
          <p data-testid="file-vide" className="border-2 border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500 md:col-span-2 xl:col-span-3">
            Aucun lot en file d'attente pour cette catégorie.
          </p>
        ) : (
          visibles.map((l) => (
            <LotCard
              key={l.id}
              lot={l}
              multi={multi}
              selected={selection.includes(l.id)}
              onToggle={toggle}
            />
          ))
        )}
      </div>

      {selLots.length > 0 && (
        <div
          data-testid="barre-action"
          className={`sticky bottom-0 flex flex-wrap items-center justify-between gap-4 border-2 px-4 py-4 ${
            familleSel === 'lampante' ? 'border-orange-600 bg-orange-50' : 'border-slate-900 bg-white'
          }`}
        >
          <div>
            <p className="font-data text-xs uppercase tracking-[0.1em] text-slate-500">
              {selLots.length} lot{selLots.length > 1 ? 's' : ''} sélectionné{selLots.length > 1 ? 's' : ''} ·{' '}
              {familleSel === 'lampante' ? 'FILIÈRE LAMPANTE' : 'FILIÈRE NOBLES'}
            </p>
            <p className="tabular font-data text-2xl font-semibold text-slate-900">
              {fmt(poidsSel)} Kg d'olives
            </p>
            {clientsSel.length > 1 && (
              <p data-testid="multi-clients-info" className="mt-1 border-l-4 border-sky-600 bg-sky-50 px-2 py-1 font-data text-[11px] text-sky-900">
                {clientsSel.length} clients dans cette sélection — l'huile se déclare passage par
                passage : aucun total commun n'est réparti entre clients.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              data-testid="demarrer-trituration"
              onClick={demarrer}
              className="flex h-14 items-center gap-2 border-2 border-slate-900 px-5 font-heading text-base font-bold text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              <Play size={20} /> Démarrer
            </button>
            <button
              type="button"
              data-testid="ouvrir-sortie-huile"
              onClick={() => setSortieOpen(true)}
              className="flex h-14 items-center gap-2 bg-emerald-700 px-5 font-heading text-base font-bold text-white transition-colors duration-150 hover:bg-emerald-600"
            >
              <Droplets size={20} /> Déclarer la sortie d'huile
            </button>
          </div>
        </div>
      )}

      {sortieOpen && selLots.length > 0 && (
        <SortieHuileDialog
          lots={selLots}
          onClose={() => {
            setSortieOpen(false);
            setSelection([]);
          }}
          onDone={load}
        />
      )}
    </div>
  );
}
